import { useLocalObservable } from 'mobx-react'
import { computedFn } from 'mobx-utils'
import { useContext } from 'react'

import logger from '@/lib/logger'
import { Bookings } from '@/types/types'
import { isOfferNotAvailableForCombinationError } from '@/utils/ExpectedError'
import StoresContext from '@/utils/StoresContext'

const useAnnotateFilters = () => {
  const { appStore } = useContext(StoresContext)

  const state = useLocalObservable(() => ({
    isCartValidWithShiftSlotPaxRoomAndSelectedHighlightedOffer: computedFn(
      (shift: Bookings.Shift, slot: Bookings.Slot, pax: number, roomId: number | null) => {
        const {
          selectedHighlightedOffer: offer,
          computeOfferFromShiftAndSlot,
          isCartValidWithOffersShiftSlotPaxAndRoom
        } = appStore.state
        if (!offer) {
          return false
        }
        try {
          const offers = [
            {
              ...computeOfferFromShiftAndSlot(offer.id, shift, slot),
              count: pax
            }
          ]
          return isCartValidWithOffersShiftSlotPaxAndRoom(offers, shift, slot, pax, roomId)
        } catch (e) {
          if (isOfferNotAvailableForCombinationError(e)) {
            // catching this error means the offer is not available for this combination of shift/slot/pax/room, no need to add a log everytime in that case
            return false
          }
          logger.info(e, 'Failed to validate cart with offer, shift, and slot', {
            offerId: offer.id,
            shiftId: shift?.id,
            slotName: slot?.slot_name,
            pax,
            roomId
          })
          return false
        }
      }
    ),
    isPaxCompatibleWithSelectedHighlightedOffer: computedFn((pax: number) => {
      const { selectedHighlightedOffer: offer, wish, currentDay } = appStore.state

      if (!offer || !currentDay) {
        return false
      }
      // loop through available slots
      return currentDay.shifts.some((shift) => {
        return shift.shift_slots.some((slot) => {
          return state.isCartValidWithShiftSlotPaxRoomAndSelectedHighlightedOffer(shift, slot, pax, wish.room_id)
        })
      })
    }),
    isRoomIdCompatibleWithSelectedHighlightedOffer: computedFn((roomId: number) => {
      const { selectedHighlightedOffer: offer, wish, currentDay } = appStore.state

      if (!offer || !currentDay) {
        return false
      }
      // loop through available slots
      return currentDay.shifts.some((shift) => {
        return shift.shift_slots.some((slot) => {
          return state.isCartValidWithShiftSlotPaxRoomAndSelectedHighlightedOffer(shift, slot, wish.pax, roomId)
        })
      })
    }),
    isShiftAndSlotCompatibleWithSelectedHighlightedOffer: computedFn((shift: Bookings.Shift, slot: Bookings.Slot) => {
      const { selectedHighlightedOffer: offer, wish } = appStore.state
      if (!offer) {
        return false
      }
      return state.isCartValidWithShiftSlotPaxRoomAndSelectedHighlightedOffer(shift, slot, wish.pax, wish.room_id)
    }),
    get isPartialWishCompatibleWithSelectedHighlightedOffer() {
      const { wish, isRoomMandatory, selectedSlot, selectedShift } = appStore.state
      const isRoomSelected = !!(wish.room_id || !isRoomMandatory)

      const isPaxSetAndCompatible = wish.pax && state.isPaxCompatibleWithSelectedHighlightedOffer(wish.pax)
      const isRoomSetAndCompatible =
        isRoomSelected && state.isRoomIdCompatibleWithSelectedHighlightedOffer(wish.room_id!)

      const isSlotAndShiftSet = !!(selectedSlot && selectedShift)
      const isSlotAndShiftSetAndCompatible =
        isSlotAndShiftSet && state.isShiftAndSlotCompatibleWithSelectedHighlightedOffer(selectedShift, selectedSlot)

      if (
        (!!wish.pax && !isPaxSetAndCompatible) ||
        (isRoomSelected && !isRoomSetAndCompatible) ||
        (isSlotAndShiftSet && !isSlotAndShiftSetAndCompatible)
      )
        return false

      return true
    }
  }))

  return state
}

export default useAnnotateFilters
